/* global gtag */
import React, { useState, useEffect, useRef } from "react";
import { BarChart2, Lightbulb } from "lucide-react";
import {
  Button, IconButton, TextField, Typography,
  Autocomplete, CircularProgress, Box, Stack,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import LogRocket from "logrocket";
import debounce from "lodash.debounce";
import { Analytics } from "@vercel/analytics/react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./App.css";
import "./ReelyAutocomplete.css";

import { supabase } from "./config/supabase";
import { TMDB_API_KEY, API_BASE, MOVIE_ALLOWLIST, SHOWCASE_CUTOFF_DATE, DEFAULT_GIF_URLS } from "./config/constants";
import { getTodayInPST, getTimeUntilNextPSTMidnight } from "./utils/dateUtils";
import { normalizeString } from "./utils/stringUtils";
import { formatFrameCountAsEmoji, formatHintCountAsEmoji, generateShareText } from "./utils/formatting";
import {
  fetchMovieId, fetchActorProfile, fetchActorProfileById,
  searchTmdb, fetchMovieDetailsById,
} from "./api/tmdb";
import {
  saveGameData, saveCompletedReel, updateUserStats as updateUserStatsInDb,
  fetchUserStats, fetchUserStreak as fetchUserStreakFromDb, fetchReelStats,
  logActorMovieUsage, logPageVisit, startNewGame as createNewGame, logGameStart,
  fetchTodayMoviePair, fetchAndStoreStartMovieCast,
  fetchAverageSteps, fetchMinSteps, fetchTopActorAndMovie, fetchUniversalTopActorsAndMovies,
} from "./api/supabaseApi";
import { StatsModal } from "./components/dialogs/StatsModal";
import { HowToPlayDialog } from "./components/dialogs/HowToPlayDialog";
import { ShareDialog } from "./components/dialogs/ShareDialog";
import { CongratsDialog } from "./components/dialogs/CongratsDialog";
import { ShowcaseView } from "./components/showcase/ShowcaseView";
import { AdBanner } from "./components/AdBanner";

LogRocket.init("pq0kyv/reely", { sampleRate: 0.02 });

// ── User cookie helpers ──────────────────────────────────────────────────────

const getUserCookie = () => {
  const cookie = document.cookie.split("; ").find((row) => row.startsWith("reely_user="));
  return cookie ? cookie.split("=")[1] : null;
};

const setUserCookie = (userId) => {
  document.cookie = `reely_user=${userId}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
};

const isValidUUID = (id) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(id);

// ── Copyright (logged once) ──────────────────────────────────────────────────

let copyrightLogged = false;
const logCopyrightNotice = () => {
  if (copyrightLogged) return;
  console.log("🛡️ © 2025 Reely. All rights reserved.");
  copyrightLogged = true;
};

// ── App ──────────────────────────────────────────────────────────────────────

function App() {

  // ── State ────────────────────────────────────────────────────────────────

  const [startMovie, setStartMovie] = useState("");
  const [endMovie, setEndMovie] = useState("");
  const [currentMovie, setCurrentMovie] = useState("");
  const [currentActor, setCurrentActor] = useState("");
  const [userInput, setUserInput] = useState("");
  const [route, setRoute] = useState([]);
  const [error, setError] = useState("");
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [steps_taken, setsteps_taken] = useState(0);
  const [startMoviePoster, setStartMoviePoster] = useState(null);
  const [moviePoster, setMoviePoster] = useState(null); // eslint-disable-line no-unused-vars
  const [endMoviePoster, setEndMoviePoster] = useState(null);
  const [searchTerm, setSearchTerm] = useState({ label: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isGameComplete, setIsGameComplete] = useState(false);
  const [theme, setTheme] = useState(""); // eslint-disable-line no-unused-vars
  const [hasInputError, setHasInputError] = useState(false); // eslint-disable-line no-unused-vars
  const [openCongratsDialog, setOpenCongratsDialog] = useState(false);
  const [openShareOptions, setOpenShareOptions] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(null);
  const [rewindCount, setRewindCount] = useState(0);
  const [endMovieId, setEndMovieId] = useState(null);
  const [showEndMovieTooltip, setShowEndMovieTooltip] = useState(false);
  const [dailyGifUrl, setDailyGifUrl] = useState("");
  const [isLoadingCast, setIsLoadingCast] = useState(false);
  const [showLongLoadMessage, setShowLongLoadMessage] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [reelStats, setReelStats] = useState(null);
  const [topActorAndMovie, setTopActorAndMovie] = useState(null);
  const [universalStats, setUniversalStats] = useState(null);
  const [startMovieCast, setStartMovieCast] = useState([]);
  const [gameId, setGameId] = useState(null);
  const [isTodayGameComplete, setIsTodayGameComplete] = useState(false);
  const [isReelHidden, setIsReelHidden] = useState(true);
  const [includeSpoilers, setIncludeSpoilers] = useState(false);
  const [hintOptions, setHintOptions] = useState([]);
  const [showHintOverlay, setShowHintOverlay] = useState(false);
  const [hintAnchorIndex, setHintAnchorIndex] = useState(null);
  const [isStartEndHint, setIsStartEndHint] = useState(false);
  const [hintSourceName, setHintSourceName] = useState("");
  const [hintCount, setHintCount] = useState(0);
  const [hintedItems, setHintedItems] = useState(new Set());
  const [showHintTooltip, setShowHintTooltip] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [showInstaPrompt, setShowInstaPrompt] = useState(false);
  const [finalFrameCount, setFinalFrameCount] = useState(null);
  const [averageFrameCount, setAverageFrameCount] = useState(null);
  const [minimumFrameCount, setMinimumFrameCount] = useState(null);
  const [showShowcase, setShowShowcase] = useState(false);
  const [showcaseDate, setShowcaseDate] = useState(null);
  const [showcaseData, setShowcaseData] = useState(null);
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => getTodayInPST()); // eslint-disable-line no-unused-vars
  const [openHowToPlay, setOpenHowToPlay] = useState(false);
  const [countdown, setCountdown] = useState(getTimeUntilNextPSTMidnight());

  // ── Refs ──────────────────────────────────────────────────────────────────

  const lastEntryRef = useRef(null);
  const topContainerRef = useRef(null);
  const tooltipRef = useRef(null);
  const hintTooltipRef = useRef(null);
  const showcaseInitRef = useRef(false);
  const fuseIndexRef = useRef({ movie: null, person: null });
  const endMovieCastRef = useRef(null);

  // ── Derived values ────────────────────────────────────────────────────────

  const parsedAverage = averageFrameCount ? Number(averageFrameCount).toFixed(1) : null;
  const pair = showcaseData && (showcaseData.popular ?? showcaseData.unique);
  const frameEmoji = formatFrameCountAsEmoji(finalFrameCount ?? steps_taken);
  const hintEmoji = formatHintCountAsEmoji(hintCount);
  const baseUrl = "https://playreely.com";

  // ── User management ───────────────────────────────────────────────────────

  const getOrCreateUser = async () => {
    let existingUserId = getUserCookie();

    if (existingUserId) {
      if (isValidUUID(existingUserId) && !existingUserId.startsWith("guest-")) {
        console.log("👤 User ID:", existingUserId);
        setUserId(existingUserId);
        return existingUserId;
      }
      document.cookie = `reely_user=; path=/; max-age=0; SameSite=Lax`;
    }

    const newUserId = uuidv4();
    console.log("👤 User ID (new):", newUserId);
    setUserCookie(newUserId);
    setUserId(newUserId);
    await supabase.from("usersv2").insert([{ user_id: newUserId }]);
    return newUserId;
  };

  const fetchUserStreak = async (uid) => {
    const resolvedUid = uid || await getOrCreateUser();
    if (!resolvedUid) return;
    const streak = await fetchUserStreakFromDb(resolvedUid);
    if (streak !== null) setCurrentStreak(streak);
  };

  // ── Initialization effects ────────────────────────────────────────────────

  useEffect(() => {
    document.title = "Reely — The Movie Connection Game";
    logCopyrightNotice();
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      const uid = await getOrCreateUser();
      if (!uid) return;
      try {
        await Promise.all([logPageVisit(uid), fetchUserStreak(uid), initializeDailyGame(uid)]);
      } catch (err) {
        console.error("❌ Error during app initialization:", err);
      }
    };
    initializeApp();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!localStorage.getItem("hasSeenHowToPlay")) {
      setOpenHowToPlay(true);
      localStorage.setItem("hasSeenHowToPlay", "true");
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("isTodayGameComplete") === "true") {
      setIsTodayGameComplete(true);
    }
  }, []);

  useEffect(() => {
    console.log("🚀 Warming up API...");
    fetch(`${API_BASE}/validate-connection?movieId=550&actorName=Brad%20Pitt`)
      .then(() => console.log("✅ API Warmed Up"))
      .catch(() => console.warn("⚠ API Warmup Failed"));
  }, []);

  // ── Countdown timer ───────────────────────────────────────────────────────

  useEffect(() => {
    const interval = setInterval(() => setCountdown(getTimeUntilNextPSTMidnight()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Daily date reset — runs on every render but only has side effects when date changes
  const todayStr = getTodayInPST();
  const lastPlayedDate = localStorage.getItem("lastPlayedDate");
  if (lastPlayedDate !== todayStr) {
    localStorage.removeItem("isTodayGameComplete");
    localStorage.removeItem("reelState");
    localStorage.setItem("lastPlayedDate", todayStr);
  }

  // ── Game state persistence ────────────────────────────────────────────────

  useEffect(() => {
    if (isGameStarted) {
      const savedState = {
        route, currentMovie, currentActor, steps_taken,
        startMovie, endMovie, startMoviePoster, endMoviePoster, endMovieId,
        isGameComplete, gameDate: getTodayInPST(),
      };
      localStorage.setItem("reelState", JSON.stringify(savedState));
    }
  }, [route, currentMovie, currentActor, steps_taken, isGameStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optimistic restore on mount — shows progress before network calls complete
  useEffect(() => {
    const saved = localStorage.getItem("reelState");
    if (!saved) return;
    const savedState = JSON.parse(saved);
    if (savedState?.gameDate !== getTodayInPST()) {
      localStorage.removeItem("reelState");
      return;
    }
    setRoute(savedState.route || []);
    setCurrentMovie(savedState.currentMovie || "");
    setCurrentActor(savedState.currentActor || "");
    setsteps_taken(savedState.steps_taken || 0);
    setStartMovie(savedState.startMovie || "");
    setEndMovie(savedState.endMovie || "");
    setStartMoviePoster(savedState.startMoviePoster || null);
    setEndMoviePoster(savedState.endMoviePoster || null);
    setEndMovieId(savedState.endMovieId || null);
    setIsGameComplete(savedState.isGameComplete || false);
    setIsGameStarted(true);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (gameId && userId) saveGameData(userId, gameId, steps_taken, false, "refreshed");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [gameId, userId, steps_taken]);

  // ── Post-game stat effects ────────────────────────────────────────────────

  useEffect(() => {
    if (isGameComplete && startMovie && endMovie) {
      fetchAverageSteps(startMovie, endMovie).then((avg) => { if (avg !== null) setAverageFrameCount(avg); });
      fetchMinSteps(startMovie, endMovie).then((min) => { if (min !== null) setMinimumFrameCount(min); });
    }
  }, [isGameComplete, startMovie, endMovie]);

  useEffect(() => {
    if (isGameComplete && typeof gtag === "function") {
      gtag("event", "completed_reel", { event_category: "Game", event_label: "Reel completed", value: steps_taken || 0 });
    }
  }, [isGameComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showStatsModal && userId) {
      fetchUserStats(userId).then(setUserStats);
      fetchReelStats(userId).then(setReelStats);
      fetchTopActorAndMovie(userId).then(async (result) => {
        if (!result) return;
        const [actorImgs, movieResults] = await Promise.all([
          Promise.all(result.topActors.map((a) => fetchActorProfile(a.name))),
          Promise.all(result.topMovies.map((m) => fetchMovieId(m.name))),
        ]);
        setTopActorAndMovie({
          actors: result.topActors.map((a, i) => ({ ...a, image: actorImgs[i] })),
          movies: result.topMovies.map((m, i) => ({ ...m, image: movieResults[i]?.poster || null })),
        });
      });
      fetchUniversalTopActorsAndMovies().then(async (result) => {
        if (!result) { setUniversalStats({ topActors: [], topMovies: [] }); return; }
        const [actorImgs, movieResults] = await Promise.all([
          Promise.all(result.topActors.map((a) => fetchActorProfile(a.name))),
          Promise.all(result.topMovies.map((m) => fetchMovieId(m.name))),
        ]);
        setUniversalStats({
          topActors: result.topActors.map((a, i) => ({ ...a, image: actorImgs[i] })),
          topMovies: result.topMovies.map((m, i) => ({ ...m, image: movieResults[i]?.poster || null })),
        });
      });
    }
  }, [showStatsModal, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isGameComplete) {
      setTimeout(() => {
        setOpenCongratsDialog(true);
        setIsGameStarted(false);
      }, 500);
    }
  }, [isGameComplete]);

  // ── UI effects ────────────────────────────────────────────────────────────

  useEffect(() => {
    const timeout = setTimeout(() => {
      lastEntryRef.current?.scrollIntoView({ behavior: "auto", inline: "end", block: "nearest" });
      topContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(timeout);
  }, [route, isGameStarted, endMovie]);

  useEffect(() => {
    if (route.length === 4 && !localStorage.getItem("seenEndMovieTooltip")) {
      setTimeout(() => setShowEndMovieTooltip(true), 2000);
    }
  }, [route]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        dismissEndMovieTooltip();
      }
    };
    if (showEndMovieTooltip) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEndMovieTooltip]);

  useEffect(() => {
    const hasSeenHintTooltip = localStorage.getItem("hasSeenHintTooltip");
    if (!hasSeenHintTooltip && isGameStarted && route.length === 1) {
      const delay = setTimeout(() => setShowHintTooltip(true), 5000);
      return () => clearTimeout(delay);
    }
  }, [isGameStarted, route.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (hintTooltipRef.current && !hintTooltipRef.current.contains(e.target)) {
        setShowHintTooltip(false);
        localStorage.setItem("hasSeenHintTooltip", "true");
      }
    };
    if (showHintTooltip) document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showHintTooltip]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".hint-overlay")) setShowHintOverlay(false);
    };
    if (showHintOverlay) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showHintOverlay]);

  // ── Showcase effects ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!showShowcase) return;
    (async () => {
      const { data: dates, error } = await supabase.rpc("list_recent_game_dates", { n: 30 });
      const today = getTodayInPST();
      let opts = [];
      if (!error && dates) {
        opts = dates
          .map((d) => d.game_date)
          .filter((d) => d >= SHOWCASE_CUTOFF_DATE)
          .sort((a, b) => b.localeCompare(a));
      }
      if (today >= SHOWCASE_CUTOFF_DATE && !opts.includes(today)) opts = [today, ...opts];
      setDateOptions(opts);
    })();
  }, [showShowcase]);

  useEffect(() => {
    if (!showShowcase) { showcaseInitRef.current = false; return; }
    if (dateOptions.length === 0) return;
    const today = getTodayInPST();
    const target = dateOptions.includes(today) ? today : dateOptions[0];
    if (!showcaseInitRef.current) {
      showcaseInitRef.current = true;
      if (showcaseDate !== target) loadShowcase(target);
    }
  }, [showShowcase, dateOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadShowcase = async (dateStr) => {
    setShowcaseDate(dateStr);
    setShowcaseData(null);

    const { data: showcase, error } = await supabase.rpc("get_daily_showcase", { date_in: dateStr });
    if (error) { console.error("get_daily_showcase error →", error); return; }
    setShowcaseData(showcase);

    const collectNames = (reel) => {
      if (!reel) return [];
      const out = new Set();
      [reel.start_movie, reel.end_movie, ...(reel.movie_chain || []), ...(reel.actor_chain || [])].forEach(
        (n) => n && out.add(n)
      );
      return Array.from(out);
    };

    const names = [...collectNames(showcase?.popular), ...collectNames(showcase?.unique)];
    if (names.length === 0) return;

    const namePayload = names.map((n) => ({
      name: n,
      type: /\(\d{4}\)\s*$/.test(n) ? "movie" : undefined,
    }));

    try {
      const resp = await fetch(`${API_BASE}/images-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ names: namePayload }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      setShowcaseData((prev) => (prev ? { ...prev, imagesByName: json?.imagesByName || {} } : prev));
    } catch (e) {
      console.error("❌ images-batch failed:", e);
    }
  };

  // ── Daily game initialization ─────────────────────────────────────────────

  const initializeDailyGame = async (uid, { autoStart = false } = {}) => {
    if (!uid) return;

    setIsGameStarted(false);
    setRoute([]);
    setUserInput("");
    setCurrentActor("");
    setMoviePoster(null);

    setError("");
    setsteps_taken(0);
    setCurrentMovie("");
    setIsGameComplete(false);

    const newGameId = await createNewGame(uid);
    setGameId(newGameId);

    const today = getTodayInPST();
    const curatedPair = await fetchTodayMoviePair(today);
    if (!curatedPair) { setError("No movie pair found for today."); return; }

    if (curatedPair.start_movie_cast) {
      try {
        const cast = Array.isArray(curatedPair.start_movie_cast)
          ? curatedPair.start_movie_cast
          : JSON.parse(curatedPair.start_movie_cast);
        setStartMovieCast(cast);
      } catch { setStartMovieCast([]); }
    } else {
      await fetchAndStoreStartMovieCast(curatedPair.start_movie_id, today);
    }

    const dayNumber = Math.floor(Date.parse(today) / 86400000);
    setDailyGifUrl(curatedPair.gif_url || DEFAULT_GIF_URLS[dayNumber % DEFAULT_GIF_URLS.length]);

    const [startMovieData, endMovieData] = await Promise.all([
      fetchMovieDetailsById(curatedPair.start_movie_id),
      fetchMovieDetailsById(curatedPair.end_movie_id),
    ]);

    if (newGameId) {
      await supabase
        .from("Games")
        .update({ start_movie: startMovieData.title, end_movie: endMovieData.title })
        .eq("id", newGameId);
    }

    setStartMovie(startMovieData.title);
    setEndMovie(endMovieData.title);
    setStartMoviePoster(startMovieData.poster);
    setEndMoviePoster(endMovieData.poster);
    setEndMovieId(endMovieData.id);
    setTheme(curatedPair.theme || "");

    // Validated restore: only restore saved session if the movies match today's pair
    const savedState = JSON.parse(localStorage.getItem("reelState"));
    const isValidSavedState =
      savedState &&
      savedState.gameDate === today &&
      savedState.startMovie === startMovieData.title &&
      savedState.endMovie === endMovieData.title;

    if (isValidSavedState && !autoStart) {
      setRoute(savedState.route || []);
      setCurrentMovie(savedState.currentMovie || "");
      setCurrentActor(savedState.currentActor || "");
      setsteps_taken(savedState.steps_taken || 0);
      setIsGameComplete(savedState.isGameComplete || false);
      setIsGameStarted(true);
    } else if (autoStart) {
      // Start a fresh game using local fresh data — avoids stale-closure issues
      // that arise when callers do `.then(() => handleStartGame())` after an async init.
      setRoute([{ name: startMovieData.title, image: startMovieData.poster, type: "movie" }]);
      setsteps_taken(0);
      setCurrentMovie(startMovieData.title);
      setHintCount(0);
      setHintedItems(new Set());
      setCurrentActor("");
      setUserInput("");
      setError("");
      setIsGameComplete(false);
      setIsGameStarted(true);
      if (newGameId) logGameStart(newGameId);
    } else {
      setRoute([]);
      setUserInput("");
      setCurrentActor("");

      setsteps_taken(0);
      setSearchTerm({ label: "" });
      setCurrentMovie(startMovieData.title);
      setIsGameComplete(false);
      setError("");
    }

    // Background preload of start movie cast
    fetch(`https://api.themoviedb.org/3/movie/${startMovieData.id}/credits?api_key=${TMDB_API_KEY}`);

    // Background prefetch of end movie cast for instant end-movie chip validation
    endMovieCastRef.current = null;
    fetch(`https://api.themoviedb.org/3/movie/${endMovieData.id}/credits?api_key=${TMDB_API_KEY}`)
      .then((r) => r.json())
      .then((json) => { endMovieCastRef.current = json.cast.map((c) => c.name.toLowerCase()); })
      .catch(() => {});
  };

  // ── Game lifecycle ────────────────────────────────────────────────────────

  const handleStartGame = () => {
    if (!startMovie || !startMoviePoster) { setError("Game is still loading movies..."); return; }
    setRoute([{ name: startMovie, image: startMoviePoster, type: "movie" }]);
    setsteps_taken(0);
    setCurrentMovie(startMovie);
    setHintCount(0);
    setHintedItems(new Set());
    setCurrentActor("");
    setUserInput("");
    setError("");
    setIsGameStarted(true);
    if (gameId) logGameStart(gameId);
  };

  const gameComplete = async (endMovieEntry) => {
    const finalRoute = [...route, endMovieEntry];
    const finalSteps = finalRoute.length - 2;
    setFinalFrameCount(finalSteps);
    setRoute(finalRoute);
    setIsGameComplete(true);
    setOpenCongratsDialog(true);

    const uid = userId || (await getOrCreateUser());
    if (gameId) saveGameData(uid, gameId, finalSteps, true, "completed");
    saveCompletedReel({ userId: uid, startMovie, endMovie, route: finalRoute, steps_taken: finalSteps, hintCount, rewindCount });

    setIsTodayGameComplete(true);
    localStorage.setItem("isTodayGameComplete", "true");

    const totalReelsCompleted = await updateUserStatsInDb(uid);
    await fetchUserStreak(uid);

    if (!localStorage.getItem("reelySeenInstaPrompt") && totalReelsCompleted >= 1) {
      setTimeout(() => setShowInstaPrompt(true), 1500);
    }

    fetchAverageSteps(startMovie, endMovie).then((avg) => { if (avg !== null) setAverageFrameCount(avg); });
    fetchMinSteps(startMovie, endMovie).then((min) => { if (min !== null) setMinimumFrameCount(min); });
  };

  // eslint-disable-next-line no-unused-vars
  const resetGame = () => {
    setRoute([]);
    setCurrentMovie("");
    setCurrentActor("");
    setUserInput("");
    setError("");
    setIsGameStarted(false);
  };

  const undoLastMove = () => {
    if (route.length === 0) { setError("No moves to undo."); return; }
    const newRoute = [...route];
    newRoute.pop();
    setUserInput("");
    setError("");
    if (newRoute.length === 0) {
      setCurrentMovie(startMovie);
      setCurrentActor("");
    } else {
      const lastEntry = newRoute[newRoute.length - 1];
      const lastName = typeof lastEntry === "string" ? lastEntry : lastEntry.name;
      if (newRoute.length === 1) {
        setCurrentMovie(startMovie);
        setCurrentActor("");
      } else if (newRoute.length % 2 === 1) {
        setCurrentMovie(lastName);
        setCurrentActor("");
      } else {
        setCurrentMovie("");
        setCurrentActor(lastName);
      }
    }
    setRoute(newRoute);
    setsteps_taken((prev) => Math.max(prev - 1, 0));
    setRewindCount((prev) => prev + 1);
  };

  // ── End movie tooltip ─────────────────────────────────────────────────────

  const dismissEndMovieTooltip = () => {
    setShowEndMovieTooltip(false);
    localStorage.setItem("seenEndMovieTooltip", "true");
  };

  const handleDismissHintTooltip = () => {
    setShowHintTooltip(false);
    localStorage.setItem("hasSeenHintTooltip", "true");
  };

  const handleDismissInstaPrompt = () => {
    setShowInstaPrompt(false);
    localStorage.setItem("reelySeenInstaPrompt", "true");
  };

  // ── End movie click ───────────────────────────────────────────────────────

  const handleEndMovieClick = async () => {
    if (!currentActor || !endMovie) return;
    try {
      let cast = endMovieCastRef.current;
      if (!cast) {
        const response = await fetch(
          `https://api.themoviedb.org/3/movie/${endMovieId}/credits?api_key=${TMDB_API_KEY}`
        );
        const data = await response.json();
        cast = data.cast.map((c) => c.name.toLowerCase());
        endMovieCastRef.current = cast;
      }
      if (cast.includes(currentActor.toLowerCase())) {
        gameComplete({ name: endMovie, image: endMoviePoster, id: endMovieId, type: "movie" });
      } else {
        setError("❌ No connection. Try again!");
      }
    } catch (err) {
      console.error("❌ Error checking end movie cast:", err);
      setError("Couldn't verify connection to end movie.");
    }
  };

  // ── Hint system ───────────────────────────────────────────────────────────

  const fetchHintOptions = async (entry, index) => {
    try {
      const normalize = (str) => str?.toLowerCase().replace(/[^\w\s]/g, "").trim();
      const usedNames = new Set(route.map((item) => normalize(item.name.replace(/\s*\(\d{4}\)$/, ""))));
      const normalizedName = normalize(entry.name);

      if (!hintedItems.has(normalizedName)) {
        setHintCount((prev) => prev + 1);
        setHintedItems((prev) => new Set(prev).add(normalizedName));
      }

      if (entry.type === "start-movie") {
        const fetchTopActors = async (movieTitle) => {
          const movieData = await fetchMovieId(movieTitle);
          if (!movieData?.id) return [];
          const res = await fetch(`https://api.themoviedb.org/3/movie/${movieData.id}/credits?api_key=${TMDB_API_KEY}`);
          const json = await res.json();
          return json.cast
            .filter((a) => a.known_for_department === "Acting" && !usedNames.has(normalize(a.name)))
            .sort((a, b) => a.order - b.order || b.popularity - a.popularity)
            .slice(0, 5)
            .map((a) => ({ name: a.name, profile: a.profile_path ? `https://image.tmdb.org/t/p/w185${a.profile_path}` : null }));
        };
        const startActors = await fetchTopActors(startMovie);
        const endActors = await fetchTopActors(endMovie);
        setHintOptions([...startActors, ...endActors]);
        setHintSourceName(`${startMovie} & ${endMovie}`);
        setIsStartEndHint(true);
        setHintAnchorIndex(null);
        setShowHintOverlay(true);
        return;
      } else {
        setIsStartEndHint(false);
      }

      if (entry.type === "movie") {
        const movieData = await fetchMovieId(entry.name);
        if (!movieData?.id) return;
        const res = await fetch(`https://api.themoviedb.org/3/movie/${movieData.id}/credits?api_key=${TMDB_API_KEY}`);
        const json = await res.json();
        const topActors = json.cast
          .filter((a) => a.known_for_department === "Acting" && !usedNames.has(normalize(a.name)))
          .sort((a, b) => a.order - b.order || b.popularity - a.popularity)
          .slice(0, 5)
          .map((a) => ({ name: a.name, profile: a.profile_path ? `https://image.tmdb.org/t/p/w185${a.profile_path}` : null }));
        setHintOptions(topActors);
        setHintSourceName(entry.name);
        setHintAnchorIndex(index);
        setShowHintOverlay(true);

      } else if (entry.type === "actor") {
        let actorId = entry.id;
        if (!actorId) {
          const res = await fetch(`https://api.themoviedb.org/3/search/person?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(entry.name)}`);
          const data = await res.json();
          if (!data.results || data.results.length === 0) return;
          const best = data.results.find((p) => p.name.toLowerCase() === entry.name.toLowerCase()) || data.results[0];
          actorId = best.id;
        }
        const creditsRes = await fetch(`https://api.themoviedb.org/3/person/${actorId}/movie_credits?api_key=${TMDB_API_KEY}`);
        const creditsData = await creditsRes.json();
        const topMovies = (creditsData.cast || [])
          .filter((m) => !usedNames.has(normalize(m.title)))
          .sort((a, b) => b.popularity - a.popularity)
          .slice(0, 5)
          .map((m) => ({
            name: `${m.title} (${m.release_date?.slice(0, 4) || "N/A"})`,
            profile: m.poster_path ? `https://image.tmdb.org/t/p/w185${m.poster_path}` : null,
          }));
        setHintOptions(topMovies);
        setHintAnchorIndex(index);
        setShowHintOverlay(true);
      }
    } catch (err) {
      console.error("❌ Error fetching hint:", err);
    }
  };

  // ── Connection validation ─────────────────────────────────────────────────

  const checkActorInStartMovieCast = (actorInput) => {
    const normalizedInput = normalizeString(actorInput);
    if (!Array.isArray(startMovieCast)) return { found: false, actorId: null };
    const match = startMovieCast.find((actor) => {
      const name = typeof actor === "string" ? actor : actor?.name;
      return normalizeString(name) === normalizedInput;
    });
    return match ? { found: true, actorId: match.id } : { found: false, actorId: null };
  };

  const polishManualActorWithinMovie = async (actorInput, movieId) => {
    try {
      const res = await fetch(`https://api.themoviedb.org/3/movie/${movieId}/credits?api_key=${TMDB_API_KEY}`);
      const data = await res.json();
      if (!data.cast || data.cast.length === 0) return { polishedName: actorInput, actorId: null, actorImage: null };
      const match = data.cast.find((a) => a.name.trim().toLowerCase() === actorInput.trim().toLowerCase());
      if (!match) return { polishedName: actorInput, actorId: null, actorImage: null };
      return {
        polishedName: match.name,
        actorId: match.id,
        actorImage: match.profile_path ? `https://image.tmdb.org/t/p/w500${match.profile_path}` : null,
      };
    } catch {
      return { polishedName: actorInput, actorId: null, actorImage: null };
    }
  };

  const polishManualMovie = async (movieInput) => {
    try {
      const movieData = await fetchMovieId(movieInput);
      if (movieData) {
        return { polishedName: movieData.titleWithYear, movieId: movieData.id, moviePoster: movieData.poster };
      }
      return { polishedName: movieInput, movieId: null, moviePoster: null };
    } catch {
      return { polishedName: movieInput, movieId: null, moviePoster: null };
    }
  };

  const handleValidConnection = async ({ movieId, actorName, resolvedActorImage, userInput, currentMovie, currentActor, actorId, isManualEntry }) => {
    const start = performance.now();
    const newEntry = { name: userInput, image: null, id: null };

    if (isManualEntry) {
      if (currentMovie) {
        const result = await polishManualActorWithinMovie(actorName, movieId);
        actorId = result.actorId;
        actorName = result.polishedName;
        resolvedActorImage = result.actorImage;
        newEntry.name = actorName;
        newEntry.id = actorId;
      } else if (currentActor) {
        const result = await polishManualMovie(userInput);
        newEntry.name = result.polishedName;
        newEntry.id = result.movieId;
        newEntry.image = result.moviePoster;
      }
    }

    if (currentMovie) {
      newEntry.id = actorId;
      newEntry.image = resolvedActorImage;
      const uid = userId || (await getOrCreateUser());
      logActorMovieUsage(uid, userInput, "actor");
    } else if (currentActor) {
      const movieData = await fetchMovieId(userInput, currentActor);
      newEntry.id = movieData?.id;
      newEntry.image = movieData?.poster || null;
      const uid = userId || (await getOrCreateUser());
      logActorMovieUsage(uid, userInput, "movie");
      if (currentMovie !== startMovie) setMoviePoster(movieData?.poster);
    }

    setError("");
    setUserInput("");
    setSearchTerm({ label: "" });

    if (currentMovie) {
      setCurrentMovie("");
      setCurrentActor(isManualEntry ? newEntry.name : userInput);
    } else {
      setCurrentMovie(isManualEntry ? newEntry.name : userInput);
      setCurrentActor("");
    }

    setTimeout(() => {
      setRoute((prevRoute) => {
        if (prevRoute.some((entry) => entry.name === newEntry.name)) return prevRoute;
        const isActor = currentMovie !== "";
        return [...prevRoute, { ...newEntry, type: isActor ? "actor" : "movie", id: newEntry.id || null }];
      });
    }, 100);

    const stripYearLocal = (title) => title?.replace(/\s*\(\d{4}\)$/, "").toLowerCase().trim();
    const isCompletingGame =
      userInput === endMovie ||
      userInput?.toLowerCase().trim() === endMovie?.toLowerCase().trim() ||
      stripYearLocal(userInput) === stripYearLocal(endMovie);

    if (isCompletingGame) {
      gameComplete({ name: endMovie, image: endMoviePoster, id: endMovieId, type: "movie" });
      return;
    }

    setsteps_taken((prev) => {
      const updated = prev + 1;
      if (gameId && userId) saveGameData(userId, gameId, updated, false);
      return updated;
    });

    try {
      LogRocket.log(`[Latency] handleValidConnection: ${Math.round(performance.now() - start)}ms`);
    } catch {}
  };

  const validateConnection = async () => {
    const start = performance.now();
    if (!currentMovie && !currentActor) { setError("Start with a valid movie or actor."); return; }

    // Duplicate check — before any API calls
    const normalizeForDupe = (str) => str?.toLowerCase().replace(/[^\w\s]/g, "").replace(/\s*\(\d{4}\)$/, "").trim();
    const inputNorm = normalizeForDupe(userInput);
    const isDuplicate = route.some((entry) => normalizeForDupe(entry.name) === inputNorm);
    if (isDuplicate) {
      setError(`You've already used that ${currentMovie ? "actor" : "movie"} in your reel!`);
      setHasInputError(true);
      setTimeout(() => setHasInputError(false), 500);
      return;
    }

    const loadingTimeout = setTimeout(() => setIsLoadingCast(true), 300);
    const longLoadTimeout = setTimeout(() => setShowLongLoadMessage(true), 3000);
    setShowLongLoadMessage(false);

    try {
      let movieId = "";
      let actorName = "";
      let resolvedActorImage = null;
      let resolvedMoviePoster = null;
      const isManualEntry = !selectedSuggestion;

      if (currentMovie) {
        actorName = userInput;
        const lastMovieInRoute = route?.[route.length - 1];
        if (lastMovieInRoute?.id) {
          movieId = lastMovieInRoute.id;
          resolvedMoviePoster = lastMovieInRoute.poster;
        } else {
          const movieData = await fetchMovieId(currentMovie, currentActor);
          movieId = movieData?.id;
          resolvedMoviePoster = movieData?.poster;
        }
        if (!movieId) { setError("Invalid input. Please enter a valid movie or actor."); return; }
        setMoviePoster(resolvedMoviePoster);

        const { found, actorId } = checkActorInStartMovieCast(userInput);
        if (currentMovie === startMovie && found) {
          if (actorId) resolvedActorImage = await fetchActorProfileById(actorId);
        } else {
          try {
            const creditsRes = await fetch(`https://api.themoviedb.org/3/movie/${movieId}/credits?api_key=${TMDB_API_KEY}`);
            const creditsData = await creditsRes.json();
            const sortedCast = creditsData.cast.sort((a, b) => a.order - b.order);
            const normalized = (str) => str?.toLowerCase().replace(/[^\w\s]/g, "").trim();
            const matchedActor =
              sortedCast.find((a) => normalized(a.name) === normalized(actorName)) ||
              sortedCast.find((a) => normalized(a.name).includes(normalized(actorName))) ||
              sortedCast.find((a) => normalized(actorName).includes(normalized(a.name)));
            if (matchedActor?.id) {
              resolvedActorImage = await fetchActorProfileById(matchedActor.id);
            } else {
              resolvedActorImage = await fetchActorProfile(actorName);
            }
          } catch {
            resolvedActorImage = await fetchActorProfile(actorName);
          }
        }
      } else if (currentActor) {
        actorName = currentActor;
        const movieData = await fetchMovieId(userInput, currentActor);
        movieId = movieData?.id;
        if (!movieId) throw new Error("Movie not found.");
        if (!resolvedMoviePoster || currentMovie === startMovie) setMoviePoster(movieData?.poster);
      }

      if (!movieId || !actorName) throw new Error("Invalid input. Please enter a valid movie or actor.");

      // Check Supabase cache first
      const { data: cachedConnection, error: cacheError } = await supabase
        .from("actor_movie_connections")
        .select("*")
        .eq("movie_id", movieId)
        .eq("actor_name", actorName)
        .limit(1);

      if (cacheError) console.error("❌ Supabase cache check error:", cacheError);

      if (cachedConnection && cachedConnection.length > 0) {
        const cachedActorId = cachedConnection[0]?.actor_id || null;
        handleValidConnection({ movieId, actorName, resolvedActorImage, userInput, currentMovie, currentActor, actorId: cachedActorId, isManualEntry });
        try { LogRocket.log("[Latency] validateConnection (cache):", { duration: Math.round(performance.now() - start), result: "cache" }); } catch {}
        return;
      }

      // Fallback to backend API
      const response = await fetch(`${API_BASE}/validate-connection?movieId=${movieId}&actorName=${actorName}`);
      const data = await response.json();

      if (data.valid) {
        let fetchedActorImage = null;
        let fetchedMoviePoster = null;

        if (data.actorId) {
          try {
            const actorRes = await fetch(`https://api.themoviedb.org/3/person/${data.actorId}?api_key=${TMDB_API_KEY}`);
            const actorDetails = await actorRes.json();
            fetchedActorImage = actorDetails?.profile_path ? `https://image.tmdb.org/t/p/w500${actorDetails.profile_path}` : null;
          } catch {}
        }

        try {
          const movieRes = await fetch(`https://api.themoviedb.org/3/movie/${movieId}?api_key=${TMDB_API_KEY}`);
          const movieDetails = await movieRes.json();
          fetchedMoviePoster = movieDetails?.poster_path ? `https://image.tmdb.org/t/p/w500${movieDetails.poster_path}` : null;
        } catch {}

        if (!isManualEntry) {
          supabase.from("actor_movie_connections").insert([{
            actor_name: actorName, actor_id: data.actorId || null,
            actor_image: fetchedActorImage || null,
            movie_name: currentMovie || userInput, movie_id: movieId,
            movie_poster: fetchedMoviePoster || null, popularity: data.popularity || null,
          }]).then(({ error }) => { if (error) console.error("❌ Supabase insert error:", error); });
        }

        await handleValidConnection({ movieId, actorName, resolvedActorImage, userInput, currentMovie, currentActor, actorId: data.actorId || null, isManualEntry });
        try { LogRocket.log("[Latency] validateConnection (tmdb):", { duration: Math.round(performance.now() - start), result: "tmdb" }); } catch {}
      } else {
        setError("❌ No connection. Try again!");
        setHasInputError(true);
        setTimeout(() => setHasInputError(false), 500);
        try { LogRocket.log("[Latency] validateConnection (invalid):", { duration: Math.round(performance.now() - start), result: "invalid" }); } catch {}
      }
    } catch (err) {
      console.error("❌ Error validating connection:", err);
      setError(err.message || "Failed to validate connection.");
    } finally {
      clearTimeout(loadingTimeout);
      clearTimeout(longLoadTimeout);
      setIsLoadingCast(false);
    }
  };

  // ── Suggestions ───────────────────────────────────────────────────────────

  useEffect(() => {
    const prefetchSuggestionsCache = async () => {
      const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const Fuse = (await import("fuse.js")).default;
      await Promise.all(["movie", "person"].map(async (type) => {
        const { data, error } = await supabase
          .from("fetch_suggestions_cache")
          .select("results")
          .eq("type", type)
          .gt("updated_at", cutoff)
          .gt("query_length", 3)
          .order("updated_at", { ascending: false })
          .limit(2000);
        if (error) { console.warn(`❌ Suggestion cache prefetch failed (${type}):`, error); return; }
        const seen = new Set();
        const deduped = (data || []).flatMap((row) => row?.results || []).filter((item) => {
          if (!item.label) return false;
          const key = item.label.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        fuseIndexRef.current[type] = new Fuse(deduped, { keys: ["label"], threshold: type === "movie" ? 0.1 : 0.15 });
        console.log(`✅ Suggestion cache ready (${type}): ${deduped.length} items`);
      }));
    };
    prefetchSuggestionsCache();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedFetchSuggestions = useRef(
    debounce(async (input, expectMovie) => {
      const start = performance.now();
      await fetchSuggestions(input, expectMovie);
      try { LogRocket.log(`[Latency] fetchSuggestions: ${Math.round(performance.now() - start)}ms`); } catch {}
    }, 150)
  ).current;

  const writeSuggestionsToCache = (query, type, results) => {
    supabase.rpc("increment_or_upsert_cache", { input_query: query, input_type: type, input_results: results })
      .then(({ error }) => { if (error) console.warn("❌ RPC cache write failed:", error); });
  };

  const fetchSuggestions = async (input, expectMovie) => {
    if (!input || input.trim().length < 2) { setSuggestions([]); return; }
    setLoading(true);

    const normalizedInput = input.trim().toLowerCase();
    const isAllowedShortMovie = expectMovie && MOVIE_ALLOWLIST.includes(normalizedInput);
    const queryType = expectMovie ? "movie" : "person";
    const safeQuery = input.toLowerCase().trim().split(/\s+/).map((w) => w.replace(/[()&|!:']/g, "")).filter(Boolean).join(" | ");

    if (isAllowedShortMovie) {
      const results = await searchTmdb(normalizedInput, queryType);
      setSuggestions(results);
      setLoading(false);
      return;
    }

    try {
      let fuseResults = [];
      const fuseIndex = fuseIndexRef.current[queryType];
      if (fuseIndex) {
        fuseResults = fuseIndex.search(input.trim()).map((r) => r.item);
      }

      if (fuseResults.length >= 6) {
        setSuggestions(fuseResults.sort((a, b) => (b.popularity || 0) - (a.popularity || 0)).slice(0, 15));
        setLoading(false);
        return;
      }

      const tmdbResults = await searchTmdb(safeQuery, queryType);
      if (tmdbResults.length > 0) writeSuggestionsToCache(safeQuery, queryType, tmdbResults);

      if (fuseResults.length > 0) {
        const allResults = [...fuseResults, ...tmdbResults];
        const seen = new Set();
        const deduped = allResults.filter((item) => {
          const key = item.label?.toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setSuggestions(deduped.sort((a, b) => (b.popularity || 0) - (a.popularity || 0)).slice(0, 15));
      } else {
        setSuggestions(tmdbResults);
      }
    } catch (err) {
      console.error("❌ Error in fetchSuggestions:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Share handlers ────────────────────────────────────────────────────────

  const getShareText = () =>
    generateShareText({ startMovie, endMovie, finalFrameCount, steps_taken, currentStreak, baseUrl, includeSpoilers, route, hintCount });

  const handleSMSShare = () => {
    if (typeof gtag === "function") gtag("event", "share_reel_method", { event_category: "Engagement", event_label: "Shared via SMS" });
    window.location.href = `sms:&body=${encodeURIComponent(getShareText())}`;
    setOpenShareOptions(false);
    try { LogRocket.track("Shared via SMS"); } catch {}
  };

  const handleWhatsAppShare = () => {
    if (typeof gtag === "function") gtag("event", "share_reel_method", { event_category: "Engagement", event_label: "Shared via WhatsApp" });
    window.open(`https://wa.me/?text=${encodeURIComponent(getShareText())}`, "_blank");
    setOpenShareOptions(false);
    try { LogRocket.track("Shared via WhatsApp"); } catch {}
  };

  const handleBlueskyShare = () => {
    if (typeof gtag === "function") gtag("event", "share_reel_method", { event_category: "Engagement", event_label: "Bluesky Share Click" });
    window.open(`https://bsky.app/intent/compose?text=${encodeURIComponent(getShareText())}`, "_blank");
    setOpenShareOptions(false);
    try { LogRocket.track("Shared via Bluesky"); } catch {}
  };

  const handleClipboardCopy = async () => {
    if (typeof gtag === "function") gtag("event", "share_reel_method", { event_category: "Engagement", event_label: "Copied to Clipboard" });
    const text = getShareText();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        alert("🔗 Copied to clipboard!");
      } else {
        alert("📎 Please tap and hold to copy:\n\n" + text);
      }
    } catch (err) {
      console.error("Clipboard error:", err);
    }
    setOpenShareOptions(false);
    try { LogRocket.track("Shared via Copied to Clipboard"); } catch {}
  };

  // ── Showcase early return ─────────────────────────────────────────────────

  if (showShowcase) {
    return (
      <ShowcaseView
        pair={pair}
        showcaseDate={showcaseDate}
        dateOptions={dateOptions}
        loadShowcase={loadShowcase}
        setShowShowcase={setShowShowcase}
        showcaseData={showcaseData}
      />
    );
  }

  // ── Main game render ──────────────────────────────────────────────────────

  return (
    <>
      <div style={{ textAlign: "center", padding: "10px", minHeight: "100svh", paddingBottom: "200px" }}>

        <Typography variant="h3" className="reely-title" style={{ fontFamily: "'Oswald', sans-serif", fontWeight: "bold !important" }}>
          REELY
        </Typography>

        <IconButton
          onClick={() => setShowStatsModal(true)}
          style={{ position: "absolute", color: isTodayGameComplete ? "#228b37" : "black", top: 10, right: 10 }}
        >
          <BarChart2 size={20} />
        </IconButton>

        <StatsModal
          open={showStatsModal}
          onClose={() => setShowStatsModal(false)}
          userStats={userStats}
          reelStats={reelStats}
          topActorAndMovie={topActorAndMovie}
          universalStats={universalStats}
        />

        <div style={{ textAlign: "center", marginBottom: "10px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <Typography variant="h6" style={{ fontFamily: "'Montserrat'", fontSize: "14px", margin: 0 }}>
              Craft a 'reel' linking the two movies.
            </Typography>
            <InfoOutlinedIcon
              style={{ fontSize: "18px", cursor: "pointer" }}
              onClick={() => setOpenHowToPlay(true)}
              titleAccess="How to Play"
            />
          </div>
        </div>

        {!isGameStarted && (
          <Box display="flex" flexDirection="row" justifyContent="center" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ width: "100%", margin: "10px 0" }}>
            {/* Start Movie */}
            <Box display="flex" flexDirection="column" alignItems="center" maxWidth="130px" flexShrink={0}>
              <img src={startMoviePoster} alt={startMovie} style={{ width: "100%", maxHeight: "180px", height: "auto", borderRadius: "5px", objectFit: "contain", display: "block" }} />
              <Box sx={{ position: "relative", width: "100%", textAlign: "center", paddingX: 1 }}>
                <Typography variant="subtitle1" mt={1} style={{ fontFamily: "Montserrat", fontSize: "12px", minHeight: "32px", textAlign: "center", lineHeight: "1.3" }}>
                  {startMovie}
                </Typography>
                {isGameStarted && route.length === 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); fetchHintOptions({ name: startMovie, type: "start-movie" }, -1); }}
                    title="Need a hint?"
                    style={{
                      position: "absolute", bottom: 1, right: 1,
                      width: 12, height: 12, borderRadius: "50%",
                      backgroundColor: "#305CDE", border: "none",
                      cursor: "pointer", padding: 0, zIndex: 2,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                    }}
                  >
                    <Lightbulb size={7} color="white" />
                  </button>
                )}
              </Box>
            </Box>

            {/* Arrow */}
            <Box display="flex" justifyContent="center" alignItems="center" height="180px">
              <Typography variant="h5" sx={{ fontFamily: "'Oswald'", fontSize: "56px", lineHeight: "1" }}>→</Typography>
            </Box>

            {/* End Movie */}
            <Box display="flex" flexDirection="column" alignItems="center" maxWidth="130px" flexShrink={0}>
              <img src={endMoviePoster} alt={endMovie} style={{ width: "100%", maxHeight: "180px", height: "auto", borderRadius: "5px", objectFit: "contain", display: "block" }} />
              <Typography variant="subtitle1" mt={1} style={{ fontFamily: "Montserrat", fontSize: "12px", minHeight: "32px", textAlign: "center", lineHeight: "1.3" }}>
                {endMovie}
              </Typography>
            </Box>
          </Box>
        )}

        {!isGameStarted ? (
          <Button
            onClick={() => {
              if (typeof gtag === "function") gtag("event", "start_game", { event_category: "Game", event_label: "Initial Start Button Clicked", value: 1 });
              handleStartGame();
            }}
            variant="contained"
            style={{ marginTop: "20px", fontFamily: "'Montserrat'" }}
          >
            Start Game
          </Button>
        ) : (
          <>
            {/* End Movie Tooltip */}
            {showEndMovieTooltip && (
              <div style={{ position: "absolute", top: "130px", left: "50%", transform: "translateX(-50%)", backgroundColor: "#ffe6e6", borderRadius: "8px", padding: "16px", boxShadow: "0px 2px 8px rgba(0,0,0,0.15)", fontSize: "14px", fontFamily: "'Montserrat'", width: "300px", zIndex: 9999, whiteSpace: "normal", textAlign: "center" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textAlign: "center" }}>
                  <div style={{ flex: 1, textAlign: "center" }}><strong>Pro Tip</strong></div>
                  <span style={{ cursor: "pointer" }} onClick={dismissEndMovieTooltip}>✕</span>
                </div>
                <div style={{ marginTop: "6px" }}>Tap the End Movie to see if your last actor finishes your reel!</div>
              </div>
            )}

            {/* Hint Tooltip */}
            {showHintTooltip && isGameStarted && route.length === 1 && (
              <div ref={hintTooltipRef} style={{ position: "absolute", top: "130px", left: "50%", transform: "translateX(-50%)", backgroundColor: "#ffe6e6", border: "1px solid #ccc", borderRadius: "8px", padding: "8px 12px", fontFamily: "'Montserrat'", fontSize: "12px", boxShadow: "0px 2px 6px rgba(0,0,0,0.15)", zIndex: 1000, maxWidth: "150px", textAlign: "center", pointerEvents: "none" }}>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Need inspiration?</strong><br /><br />
                  Click the hint button on the card for hints!
                </div>
                <div style={{ position: "absolute", top: "4px", right: "4px", cursor: "pointer", fontWeight: "bold", fontSize: "10px", color: "#666" }} onClick={handleDismissHintTooltip}>✖</div>
              </div>
            )}

            <div ref={topContainerRef}>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "4px", marginBottom: "6px" }}>
                {isGameStarted && (
                  <Typography variant="body1" style={{ fontSize: "12px", fontFamily: "'Montserrat'" }}>
                    Frames: {steps_taken} | Hints: {hintCount}
                  </Typography>
                )}
              </div>

              {/* Route Display */}
              <div style={{ overflowX: "auto", scrollBehavior: "smooth", whiteSpace: "nowrap", paddingTop: "2px", paddingBottom: "2px", marginBottom: "20px", width: "100%", maxWidth: "100vw" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", minWidth: "max-content" }}>
                  {route.map((entry, index) => {
                    const isLast = index === route.length - 1;
                    return (
                      <React.Fragment key={index}>
                        {index > 0 && <Typography variant="h5" style={{ margin: "0 10px", display: "inline-block" }}>→</Typography>}
                        <div style={{ position: "relative", width: 100, borderRadius: 8, backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", textAlign: "center", flexShrink: 0 }}>
                          {entry.image && <img src={entry.image} alt={entry.name} style={{ width: "100%", height: 120, objectFit: "cover", display: "block", borderRadius: "8px 8px 0 0" }} />}
                          <div style={{ height: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 8px" }}>
                            <Typography variant="body2" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "12px", lineHeight: 1.3, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden", textOverflow: "ellipsis", wordWrap: "break-word", whiteSpace: "normal", textAlign: "center" }}>
                              {entry.name}
                            </Typography>
                          </div>
                          {isGameStarted && isLast && (
                            <button
                              onClick={(e) => { e.stopPropagation(); fetchHintOptions(entry, index); }}
                              title="Need a hint?"
                              style={{
                                position: "absolute", bottom: 1, right: 1,
                                width: 12, height: 12, borderRadius: "50%",
                                backgroundColor: "#305CDE", border: "none",
                                cursor: "pointer", padding: 0, zIndex: 2,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                              }}
                            >
                              <Lightbulb size={7} color="white" />
                            </button>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {route.length > 0 && (
                    <Typography variant="h5" sx={{ margin: "0 10px", display: "inline-block", lineHeight: "1", fontFamily: "'Oswald'", fontWeight: "400", color: "#999" }}>• • •</Typography>
                  )}

                  {/* End Movie Card */}
                  <div
                    ref={lastEntryRef}
                    onClick={handleEndMovieClick}
                    style={{ position: "relative", width: 100, borderRadius: 8, backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", textAlign: "center", opacity: 0.4, cursor: "pointer", transition: "opacity 0.2s", flexShrink: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.6"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.4"; }}
                  >
                    <img src={endMoviePoster} alt={endMovie} style={{ width: "100%", height: 120, objectFit: "cover", display: "block", borderRadius: "8px 8px 0 0" }} />
                    <div style={{ height: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 8px" }}>
                      <Typography variant="body2" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: "12px", lineHeight: 1.3, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden", textOverflow: "ellipsis", wordWrap: "break-word", whiteSpace: "normal", textAlign: "center" }}>
                        {endMovie}
                      </Typography>
                    </div>
                    {isGameStarted && (
                      <button
                        onClick={(e) => { e.stopPropagation(); fetchHintOptions({ name: endMovie, type: "movie" }, -1); }}
                        title="Need a hint?"
                        style={{
                          position: "absolute", bottom: 1, right: 1,
                          width: 12, height: 12, borderRadius: "50%",
                          backgroundColor: "#305CDE", border: "none",
                          cursor: "pointer", padding: 0, zIndex: 2,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                        }}
                      >
                        <Lightbulb size={7} color="white" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Hint Overlay */}
            {showHintOverlay && hintOptions.length > 0 && (
              <div className="hint-overlay" style={{ position: "absolute", top: "20%", left: "0", right: "0", margin: "0 auto", maxWidth: "95vw", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "8px", padding: "16px", zIndex: 9999, boxShadow: "0px 4px 12px rgba(0,0,0,0.2)", fontFamily: "'Montserrat'" }}>
                <Typography variant="subtitle2" sx={{ fontSize: "18px", fontFamily: "'Montserrat'", marginBottom: "8px" }}>
                  {isStartEndHint ? (
                    <>Actors in <em>{startMovie}</em> and <em>{endMovie}</em></>
                  ) : hintAnchorIndex !== null && route[hintAnchorIndex]?.type === "movie" ? (
                    <>Actors in <em>{route[hintAnchorIndex]?.name}</em></>
                  ) : hintAnchorIndex !== null && route[hintAnchorIndex]?.type === "actor" ? (
                    <>Movies with {route[hintAnchorIndex]?.name}</>
                  ) : hintSourceName ? (
                    <>Actors in <em>{hintSourceName}</em></>
                  ) : null}
                </Typography>
                <Stack spacing={1}>
                  {hintOptions.map((actor, i) => (
                    <div key={i}
                      onClick={() => { setUserInput(actor.name); setSearchTerm({ label: actor.name }); setShowHintOverlay(false); }}
                      style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px", fontFamily: "'Montserrat'", padding: "6px 4px", borderRadius: "6px", transition: "background-color 0.2s", color: "#333" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      {actor.profile && <img src={actor.profile} alt={actor.name} style={{ width: "36px", height: "36px", borderRadius: "50%" }} />}
                      <span>{actor.name}</span>
                    </div>
                  ))}
                </Stack>
              </div>
            )}

            {/* Error */}
            {error && (
              <Typography color="error" style={{ marginBottom: "8px", marginTop: "-4px", fontFamily: "'Montserrat'", fontSize: "12px" }}>
                {error}
              </Typography>
            )}

            {/* Loading Spinner */}
            {isLoadingCast && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "10px" }}>
                <div className="spinner" />
                {showLongLoadMessage && (
                  <Typography variant="body2" sx={{ marginTop: "8px", fontFamily: "'Montserrat'", fontSize: "12px", color: "#999" }}>
                    Production delays - hang tight!
                  </Typography>
                )}
              </div>
            )}

            {/* Search Input */}
            <div style={{ marginTop: "15px", marginBottom: "15px" }}>
              <Typography
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "12px",
                  fontStyle: "italic",
                  color: "#666",
                  marginBottom: "6px",
                  textAlign: "left",
                }}
              >
                {currentMovie ? `Enter an actor who appears in ${currentMovie}` : `Enter a movie in which ${currentActor} appears`}
              </Typography>
              <Autocomplete
                freeSolo
                fullWidth
                options={suggestions}
                value={searchTerm || ""}
                getOptionLabel={(option) => option.label || ""}
                filterOptions={(x) => x}
                onInputChange={(event, newInput, reason) => {
                  const safeInput = newInput ?? "";
                  setSearchTerm({ label: safeInput });
                  setUserInput(safeInput);
                  if (reason === "input" || reason === "clear") setSelectedSuggestion(null);
                  debouncedFetchSuggestions(safeInput, currentActor !== "");
                }}
                onChange={(event, newValue) => { if (newValue) setSelectedSuggestion(newValue.label); }}
                loading={loading}
                loadingText={<span style={{ fontStyle: "italic", fontSize: "14px", fontFamily: "'Montserrat'" }}>Loading...</span>}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return <li key={option.id || option.label} {...rest} className="autocompleteOption">{option.label}</li>;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); validateConnection(); } }}
                    style={{ backgroundColor: "white", fontFamily: "'Montserrat'" }}
                    InputProps={{
                      ...params.InputProps,
                      style: { fontFamily: "'Montserrat'", fontSize: "16px", fontWeight: 500, color: "#333", paddingTop: "10px", paddingBottom: "10px" },
                      endAdornment: (<>{loading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
                    }}
                    inputProps={{ ...params.inputProps, style: { fontFamily: "'Montserrat'", fontSize: "16px", fontWeight: 500, color: "#333" } }}
                  />
                )}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ marginBottom: "10px" }}>
                <Button onClick={validateConnection} variant="contained" style={{ fontFamily: "'Montserrat'" }}>
                  Add to Reel
                </Button>
              </div>
              {route.length > 1 && (
                <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                  <Button onClick={undoLastMove} variant="outlined" style={{ fontFamily: "'Montserrat'" }}>⏪ Rewind</Button>
                  <Button
                    onClick={() => {
                      if (typeof gtag === "function") gtag("event", "start_over", { event_category: "Game", event_label: "Start Over Clicked", value: 1 });
                      if (gameId && userId) saveGameData(userId, gameId, steps_taken, false, "new_movie_clicked");
                      const uid = userId;
                      initializeDailyGame(uid, { autoStart: true });
                    }}
                    variant="outlined"
                    style={{ fontFamily: "'Montserrat'" }}
                  >
                    🔄 Start Over
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{ textAlign: "center", fontFamily: "'Montserrat'", fontSize: "12px", padding: "20px", color: "#666" }}>
          Reely uses data from{" "}
          <a href="https://www.themoviedb.org/" target="_blank" rel="noopener noreferrer" style={{ color: "#3f51b5", textDecoration: "underline" }}>TMDB</a>.
          <br />
          We're in beta —{" "}
          <a href="https://forms.gle/2S2pVvDajVmX7KWJ7" target="_blank" rel="noopener noreferrer" style={{ color: "#3f51b5", textDecoration: "underline" }}>share feedback</a>!
          <br />
          <a href="https://www.reddit.com/r/playreely" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", color: "#3f51b5", textDecoration: "underline", marginTop: "6px" }}>
            <img src="/reddit_logo.png" alt="Reddit" style={{ width: "18px", height: "18px", marginRight: "6px" }} />
          </a>
          <a href="https://instagram.com/playreely" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", color: "#3f51b5", textDecoration: "underline", marginTop: "6px" }}>
            <img src="/instagram_icon.png" alt="Instagram" style={{ width: "18px", height: "18px", marginRight: "6px" }} />
          </a>
          <AdBanner />
        </div>
      </div>

      {/* Dialogs */}
      <CongratsDialog
        open={openCongratsDialog}
        onClose={() => setOpenCongratsDialog(false)}
        startMovie={startMovie}
        endMovie={endMovie}
        finalFrameCount={finalFrameCount}
        frameEmoji={frameEmoji}
        hintEmoji={hintEmoji}
        hintCount={hintCount}
        parsedAverage={parsedAverage}
        minimumFrameCount={minimumFrameCount}
        isReelHidden={isReelHidden}
        onToggleReel={() => setIsReelHidden(!isReelHidden)}
        route={route}
        currentStreak={currentStreak}
        countdown={countdown}
        dailyGifUrl={dailyGifUrl}
        showInstaPrompt={showInstaPrompt}
        onDismissInstaPrompt={handleDismissInstaPrompt}
        onPlayAgain={() => {
          setOpenCongratsDialog(false);
          const uid = userId;
          initializeDailyGame(uid, { autoStart: true });
        }}
        onShareClick={() => setOpenShareOptions(true)}
        onRevealShowcase={() => {
          const today = getTodayInPST();
          setSelectedDate(today);
          loadShowcase(today);
          setShowShowcase(true);
        }}
      />

      <ShareDialog
        open={openShareOptions}
        onClose={() => setOpenShareOptions(false)}
        includeSpoilers={includeSpoilers}
        onToggleSpoilers={setIncludeSpoilers}
        onSMSShare={handleSMSShare}
        onWhatsAppShare={handleWhatsAppShare}
        onBlueskyShare={handleBlueskyShare}
        onClipboardCopy={handleClipboardCopy}
      />

      <HowToPlayDialog open={openHowToPlay} onClose={() => setOpenHowToPlay(false)} />

      <Analytics />
    </>
  );
}

export default App;
